import React, { Suspense, useEffect, useState } from 'react';
import { Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { AppLayout, ContentLayout, Spinner, Header as CloudscapeHeader, BreadcrumbGroup, Link, Flashbar } from '@amzn/awsui-components-react';
import ServiceCollection from 'src/services/ServiceCollection';
import { UserProfile } from 'src/models/users/UserProfile';
import { UserIdentity } from 'src/models/users/UserIdentity';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { GlobalState, initialState } from 'src/services/global/GlobalState';
import { globalReducer, GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import TPEAction from 'src/models/common/TPEAction';
import 'src/assets/styles/EntityStatusBar.scss';
import 'src/assets/styles/Dropdowns.scss';
import 'src/assets/styles/Inputs.scss';
import 'src/assets/styles/Buttons.scss';
import 'src/assets/styles/Radios.scss';
import 'src/assets/styles/Containers.scss';
import 'src/assets/styles/Layouts.scss';
import 'src/assets/styles/TextFilters.scss';
import 'src/assets/styles/Tabs.scss';
import 'src/assets/styles/Spans.scss';
import 'src/assets/styles/Divs.scss';
import 'src/assets/styles/Popovers.scss';
import 'src/assets/styles/StatusIndicators.scss';
import 'src/assets/styles/Autosuggests.scss';
import 'src/assets/styles/BulkAction.scss';
import { AppRoles } from 'src/models/permissions/RolePermissions';
import CONSTANTS from 'src/utils/constants';
import Navigation from './Navigation';
import AppHeader from './AppHeader';
import './app.scss';
import { constructBreadcrumbs, getPageHeaderMapping, navigateTo } from './AppBreadcrumbs';
import { PageHeaderMapping } from 'src/models/navigation/PageHeaderMapping';
import ICAssist from './help-panels/ICAssist';
import ATPHelpPanel from './help-panels/ATPHelpPanel';
import StringUtils from 'src/utils/stringUtils';
import getRoutes from './AppRoutes';
import APP_CONSTANTS from './appConstants';


/**
 * Defines the properties available inside the global context
 */
export type GlobalContextType = {
    globalState: GlobalState,
    globalDispatch: React.Dispatch<TPEAction>,
    userProfile: UserProfile,
    services: ServiceCollection
}

/**
 * It takes care of "injecting" the context value in the children
 * @param props Context provider props
 * @returns The children of this fuincional component wrapped with the ContextProvided value
 */
const GlobalContexProvider = (props: any) => {
    const { state, dispatch, userProfile, services, children } = props;
    const providerValue = React.useMemo(() => ({
        globalState: state,
        globalDispatch: dispatch,
        userProfile,
        services
    }), [state, dispatch, userProfile]);
    return (
        <GlobalAppContext.Provider value={providerValue}>
            {children}
        </GlobalAppContext.Provider>
    );
}

/**
 * The global context being shared to any component that needs it
 */
export const GlobalAppContext = React.createContext(null as unknown as GlobalContextType);

export default function App(props: { serviceCollection: ServiceCollection, userIdentity: UserIdentity }) {
    const { serviceCollection, userIdentity } = props;
    const [userProfile, userProfileLoading, userProfileError] = serviceCollection.usersService.getUserProfile(userIdentity.alias);
    const [sharedUserProfile, setSharedUserProfile] = useState(null as unknown as UserProfile);
    const [globalState, globalDispatch] = useReducerWithLogger(globalReducer, initialState);
    const { pageSidebarKey, dynamicBreadcrumb, bannerItems } = globalState;
    // This is a silent save. No action is needed if it fails or succeeds
    const [saveUserProfileResult, isSavingUserProfile, saveUserProfileError] = serviceCollection.usersService.saveUserProfile(sharedUserProfile);

    document.body.classList.remove('white-background');

    const [navigationOpen, setNavigationOpen] = useState(false);
    const [activeDrawerId, setActiveDrawerId] = useState(null as unknown as string);

    const history = useHistory();
    const location: any = useLocation();
    const pageHeaderMapping: PageHeaderMapping = getPageHeaderMapping(location.pathname);
    const localBreadcrumb = (dynamicBreadcrumb != null && dynamicBreadcrumb.path == location.pathname) ? dynamicBreadcrumb : null;
    const displayBreadcrumbs = constructBreadcrumbs(location.pathname, location.state?.breadcrumbs, dynamicBreadcrumb);
    const displayPageTitle = localBreadcrumb?.pageTitle != null ? localBreadcrumb.pageTitle : pageHeaderMapping.pageTitle;
    const displayPageDesc = localBreadcrumb?.pageDescription != null ? localBreadcrumb.pageDescription : pageHeaderMapping.pageDescription;

    useEffect(() => {
        // Initializing the service for banner methods
        serviceCollection.messageService.setupForBanner(globalDispatch,GLOBAL_ACTIONS.ADD_BANNER_MESSAGE, GLOBAL_ACTIONS.REMOVE_BANNER_MESSAGE);
    }, []);

    useEffect(() => {
        if (userProfile == null) {
            return;
        }
        userProfile.firstName = userIdentity.givenName;
        userProfile.lastName = userIdentity.familyName;
        userProfile.user = userIdentity.alias;
        userProfile.roles = userIdentity.roles;
        userProfile.authToken = userIdentity.token;
        setSharedUserProfile(userProfile);
    }, [userProfile]);

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(pageSidebarKey)) {
            setActiveDrawerId(APP_CONSTANTS.DRAWERS.HELP);
        }
    }, [pageSidebarKey])

    useEffect(() => {
        // Close the help panel when navigating to a differnt page
        if (activeDrawerId == APP_CONSTANTS.DRAWERS.HELP) {
            setActiveDrawerId(null as unknown as string);
            globalDispatch(GLOBAL_ACTIONS.HIDE_SIDEBAR);
        }
    }, [location.pathname])

    const isNafnUser = serviceCollection.usersService.hasRole(AppRoles.NAFN, userProfile?.roles);

    const handleDrawerChange = (activeDrawerId: string | null) => {
        setActiveDrawerId(activeDrawerId as unknown as string);
        if (activeDrawerId != APP_CONSTANTS.DRAWERS.HELP) {
            globalDispatch(GLOBAL_ACTIONS.HIDE_SIDEBAR);
        }
    }

  const contentBody = () => (
    <Suspense fallback={<Spinner size="large" />}>
        <ContentLayout
              breadcrumbs={location.pathname == CONSTANTS.PAGE_NAV.DASHBOARD.URL ? null : <BreadcrumbGroup 
                                items={displayBreadcrumbs}
                                onClick={(event: any) => {
                                  event.preventDefault();
                                  navigateTo(history, location.pathname, event.detail.href, location.state, globalState);
                              }}/>}
              defaultPadding={true}
              headerVariant={"default"}
              disableOverlap={true}
              notifications={<Flashbar items={bannerItems} />}
              header={location.pathname == CONSTANTS.PAGE_NAV.DASHBOARD.URL ? null :
                    <CloudscapeHeader
                        variant="h1"
                        description={displayPageDesc || 'Automated Transfer Pricing'}
                        actions={localBreadcrumb?.headerActions}
                        info={
                            pageHeaderMapping?.pageSidebarKey ? (
                                <Link variant="info" onFollow={() => {
                                    globalDispatch(GLOBAL_ACTIONS.SHOW_SIDEBAR.withPayload(pageHeaderMapping?.pageSidebarKey));
                                }}>Info</Link>
                            ) : null
                        }
                        >
                          {displayPageTitle || 'Automated Transfer Pricing'} <sub className="headerSubscript">{localBreadcrumb?.headerSubscript}</sub>
                        </CloudscapeHeader>}
              >
            <Switch>
                { getRoutes(serviceCollection, userProfile) }
                { isNafnUser ? <Redirect path="*" to="/calculations/readOnly" /> : <Redirect path="*" to="/" /> }
            </Switch>
        </ContentLayout>
    </Suspense>
  );

    return (
        <div className="root">
            <GlobalContexProvider state={globalState} dispatch={globalDispatch} userProfile={sharedUserProfile} services={serviceCollection}>
            <AppHeader />
            <AppLayout
                headerSelector="#header"
                ariaLabels={{}}
                content={contentBody()}
                navigation={<Navigation />}
                navigationOpen={navigationOpen}
                onNavigationChange={() => setNavigationOpen(!navigationOpen)}
                disableContentPaddings={true}
                activeDrawerId={activeDrawerId}
                onDrawerChange={({ detail: { activeDrawerId } }) => handleDrawerChange(activeDrawerId)}
                drawers={[
                    {
                        id: APP_CONSTANTS.DRAWERS.HELP,
                        content: <ATPHelpPanel />,
                        ariaLabels: {drawerName: APP_CONSTANTS.DRAWERS.HELP},
                        trigger: {
                            iconName: "status-info"
                        }
                    },
                    {
                        id: APP_CONSTANTS.DRAWERS.CHATBOT,
                        content: <ICAssist/>,
                        ariaLabels: { drawerName: APP_CONSTANTS.DRAWERS.CHATBOT },
                        trigger: {
                            iconName: "gen-ai"
                        }
                    }
                ]}
            />
            </GlobalContexProvider>
    </div>
    );
}

